import dayjs, { Dayjs } from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IFitbitSleepStats } from "../../../../../../@types/Fitbit";
import { LineDot } from "../../../../../../assets/icons/LineDot";
import { StyledLegend } from "../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import { formatMeasurementAverageRangeDates } from "../../../../../../utils/chartUtils";
import {
  Container,
  DateLabel,
  Divider,
  LegendItems,
  NumberLabel,
  SummariesContainer,
  SummaryItem,
  TextLabel,
} from "./CustomFitbitSleepLegend.styles";
import { FitbitSleepChartRow } from "./FitbitSleepChart";

type CustomFitbitSleepLegendProps = {
  data: FitbitSleepChartRow[];
  statistics?: IFitbitSleepStats;
  startDate: Dayjs;
  endDate: Dayjs;
  providerLabel?: string | null;
};

export function CustomFitbitSleepLegend({
  data,
  statistics,
  startDate,
  endDate,
  providerLabel,
}: CustomFitbitSleepLegendProps) {
  const { t } = useTranslation();

  const { disabled, average, highest, lowest } = useMemo(() => {
    const hasData = Array.isArray(data) && data.length > 0;

    if (!hasData) {
      return {
        disabled: true,
        average: null,
        highest: null,
        lowest: null,
      };
    }

    const sorted = [...data].sort((a, b) => b.hoursAsleep - a.hoursAsleep);
    const highestRow = sorted[0];
    const lowestRow = sorted[sorted.length - 1];
    const avgValue =
      statistics?.averageHoursAsleep ??
      data.reduce((acc, d) => acc + d.hoursAsleep, 0) / data.length;
    const highestValue =
      statistics?.highestHoursAsleep ?? highestRow.hoursAsleep;
    const lowestValue = statistics?.lowestHoursAsleep ?? lowestRow.hoursAsleep;

    const formatDate = (row: FitbitSleepChartRow) =>
      dayjs(row.timeValue).format("MMM D, YYYY");

    return {
      disabled: false,
      average: {
        value: avgValue.toFixed(1),
        date: formatMeasurementAverageRangeDates(startDate, endDate),
      },
      highest: {
        value: highestValue.toFixed(1),
        date: formatDate(highestRow),
      },
      lowest: {
        value: lowestValue.toFixed(1),
        date: formatDate(lowestRow),
      },
    };
  }, [data, statistics, startDate, endDate]);

  return (
    <div style={{ bottom: -16, position: "relative" }}>
      <Container>
        <SummariesContainer>
          <SummaryItem>
            <NumberLabel disabled={disabled}>
              {average?.value ?? "-"}
            </NumberLabel>
            <TextLabel disabled={disabled}>
              {t("patientPage_measurementModal_legend_average")}
            </TextLabel>
            <DateLabel disabled={disabled}>{average?.date ?? "-"}</DateLabel>
          </SummaryItem>
          <SummaryItem>
            <NumberLabel disabled={disabled}>
              {highest?.value ?? "-"}
            </NumberLabel>
            <TextLabel disabled={disabled}>
              {t("patientPage_measurementModal_legend_highest")}
            </TextLabel>
            <DateLabel disabled={disabled}>{highest?.date ?? "-"}</DateLabel>
          </SummaryItem>
          <SummaryItem>
            <NumberLabel disabled={disabled}>
              {lowest?.value ?? "-"}
            </NumberLabel>
            <TextLabel disabled={disabled}>
              {t("patientPage_measurementModal_legend_lowest")}
            </TextLabel>
            <DateLabel disabled={disabled}>{lowest?.date ?? "-"}</DateLabel>
          </SummaryItem>
        </SummariesContainer>

        <Divider />

        <LegendItems disabled={disabled}>
          <StyledLegend key="sleep">
            <LineDot fill="#54A6AA" />
            {providerLabel ?? "Fitbit"}
          </StyledLegend>
        </LegendItems>
      </Container>
    </div>
  );
}
